import { useState } from "react";
import { Radio, Calendar, MapPin, Hammer, ChevronDown } from "lucide-react";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function StatusBadge({ status }) {
  if (status === "live")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        <Radio className="w-3 h-3 live-dot" /> LIVE
      </span>
    );
  if (status === "final")
    return <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-700/50 text-slate-300 border border-slate-600">FINAL</span>;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-smasher-gold/15 text-smasher-gold border border-smasher-gold/30">UPCOMING</span>;
}

function TeamRow({ team, score, showScore, winner }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5 min-w-0">
        {team.logo && !broken ? (
          <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 border border-white/10 overflow-hidden">
            <img src={team.logo} alt={team.abbr} className="w-6 h-6 object-contain" loading="lazy" onError={() => setBroken(true)} />
          </span>
        ) : (
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black text-white shrink-0 border border-white/10"
            style={{ backgroundColor: team.color }}
          >
            {team.abbr}
          </span>
        )}
        <span className={`truncate font-semibold ${winner ? "text-white" : "text-slate-300"}`}>{team.name}</span>
      </div>
      {showScore && (
        <span className={`font-mono-num text-lg font-bold ${winner ? "text-smasher-gold" : "text-slate-400"}`}>{score}</span>
      )}
    </div>
  );
}

function GameNotes({ notes, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-white/5 pt-2.5">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-[11px] font-bold text-smasher-gold hover:text-smasher-orange transition-colors" data-testid={`game-notes-toggle-${id}`}>
        <span className="inline-flex items-center gap-1.5"><Hammer className="w-3.5 h-3.5" /> SMASHER GAME NOTES ({notes.length})</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`game-notes-${id}`}>
          {notes.map((n, i) => (
            <div key={i} className="bg-black/40 rounded-lg p-2.5">
              <div className="flex justify-between text-[11px] font-bold text-white"><span>{n.label}</span>{n.odds && <span className="font-mono-num text-emerald-400">{n.odds}</span>}</div>
              <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{n.summary}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EventCard({ event }) {
  const { status, league, team_a, team_b, start, venue, spread, total, moneyline, score_a, score_b, period } = event;
  const showScore = status === "final" || status === "live";
  const hasOdds = spread || total || moneyline;
  return (
    <div className="panel panel-hover p-4 flex flex-col gap-3" data-testid={`event-card-${event.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold tracking-widest text-smasher-gold">{league}</span>
        <StatusBadge status={status} />
      </div>
      <div className="space-y-2.5">
        <TeamRow team={team_a} score={score_a} showScore={showScore} winner={showScore && score_a >= score_b} />
        <div className="text-[10px] text-slate-600 font-bold text-center">VS</div>
        <TeamRow team={team_b} score={score_b} showScore={showScore} winner={showScore && score_b > score_a} />
      </div>

      {status === "live" && period && (
        <div className="text-center text-xs font-mono-num text-emerald-400">{period}</div>
      )}

      {status === "upcoming" && (
        <div className="flex items-center gap-3 text-xs text-slate-400 border-t border-white/5 pt-2">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(start)} · {fmtTime(start)}</span>
        </div>
      )}
      {venue && (
        <div className="inline-flex items-center gap-1 text-[11px] text-slate-500">
          <MapPin className="w-3 h-3" /> {venue}
        </div>
      )}

      {hasOdds && (
        <div className="grid grid-cols-3 gap-1.5 border-t border-white/5 pt-2.5">
          {[["SPREAD", spread], ["TOTAL", total], ["ML", moneyline]].map(([k, v]) => (
            <div key={k} className="text-center bg-black/40 rounded-lg py-1.5">
              <div className="text-[9px] text-slate-500 tracking-wide">{k}</div>
              <div className="text-[11px] font-mono-num font-bold text-white truncate px-1">{v || "—"}</div>
            </div>
          ))}
        </div>
      )}
      {event.notes?.length > 0 && <GameNotes notes={event.notes} id={event.id} />}
    </div>
  );
}
